import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, HERO_IMG, BACKEND_URL } from "@/lib/api";

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND_URL}${u}`;
};

const FALLBACK = {
  image: "",
  overline: "The Salon Edit · No. 01",
  heading_line_1: "Professional",
  heading_line_2: "Salon Care",
  heading_line_3: "at Home.",
  description:
    "Premium salon-quality beauty rituals — formulated in small batches, designed for flawless, editorial-finish results.",
  primary_cta_label: "Shop Now",
  primary_cta_link: "/shop",
  secondary_cta_label: "Explore Collection",
  secondary_cta_link: "/shop",
  card_label: "Best Seller",
  card_title: "24K Gold Radiance Facial Kit",
  card_price: "From ₹2,899",
  card_cta_label: "Discover",
  card_cta_link: "/shop",
  stat_1_value: "12+",
  stat_1_label: "Salon Awards",
  stat_2_value: "1.4k",
  stat_2_label: "Salons Trust Us",
  stat_3_value: "98%",
  stat_3_label: "Repeat Clients",
};

export default function Hero() {
  const [hero, setHero] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/site/hero")
      .then((r) => {
        const d = r.data || {};
        setHero({ ...FALLBACK, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== "" && v !== null && v !== undefined)) });
      })
      .catch(() => {});
  }, []);

  const heroImg = hero.image ? absoluteUrl(hero.image) : HERO_IMG;
  const stats = [
    { v: hero.stat_1_value, l: hero.stat_1_label },
    { v: hero.stat_2_value, l: hero.stat_2_label },
    { v: hero.stat_3_value, l: hero.stat_3_label },
  ].filter((s) => s.v || s.l);

  return (
    <section className="relative bg-white pt-32 md:pt-28 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-[88vh]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="lg:col-span-6 relative z-10"
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="divider-gold" />
            <span className="overline" data-testid="hero-overline">{hero.overline}</span>
          </div>

          <h1 className="font-serif-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-light" data-testid="hero-heading">
            {hero.heading_line_1}
            {hero.heading_line_2 && (
              <>
                <br />
                <span className="italic gold-text">{hero.heading_line_2}</span>
              </>
            )}
            {hero.heading_line_3 && (
              <>
                <br />
                {hero.heading_line_3}
              </>
            )}
          </h1>

          <p className="font-body text-base md:text-lg text-[#3a3a3a] mt-8 max-w-md leading-relaxed" data-testid="hero-description">
            {hero.description}
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-10">
            {hero.primary_cta_label && (
              <Link to={hero.primary_cta_link || "/shop"} className="btn-primary" data-testid="hero-shop-now">
                {hero.primary_cta_label} <ArrowRight size={14} />
              </Link>
            )}
            {hero.secondary_cta_label && (
              <Link to={hero.secondary_cta_link || "/shop"} className="btn-outline" data-testid="hero-explore-collection">
                {hero.secondary_cta_label}
              </Link>
            )}
          </div>

          {stats.length > 0 && (
            <div className="grid grid-cols-3 gap-6 mt-14 max-w-md" data-testid="hero-stats">
              {stats.map((s, i) => (
                <div key={i}>
                  <div className="font-serif-display text-3xl">{s.v}</div>
                  <div className="overline mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F8F5F0]">
            <img
              src={heroImg}
              alt={hero.card_title ? `${hero.card_title} — Blisson hero` : "Blisson Professional hero"}
              decoding="async"
              className="w-full h-full object-cover"
              data-testid="hero-model-image"
            />
            <div className="absolute inset-0 grain pointer-events-none" />

            {(hero.card_title || hero.card_label) && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="absolute bottom-6 left-6 right-6 md:left-8 md:right-auto md:bottom-8 glass-dark text-white p-6 md:max-w-[300px]"
                data-testid="hero-floating-card"
              >
                {hero.card_label && <div className="overline text-white/70 mb-2" data-testid="hero-card-label">{hero.card_label}</div>}
                {hero.card_title && (
                  <div className="font-serif-display text-xl leading-tight" data-testid="hero-card-title">{hero.card_title}</div>
                )}
                <div className="flex items-center justify-between mt-3">
                  {hero.card_price && <span className="text-sm" data-testid="hero-card-price">{hero.card_price}</span>}
                  {hero.card_cta_label && (
                    <Link
                      to={hero.card_cta_link || "/shop"}
                      className="text-[#C9A961] text-[11px] tracking-[0.3em] uppercase"
                      data-testid="hero-card-cta"
                    >
                      {hero.card_cta_label}
                    </Link>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      <div className="absolute -right-32 top-40 text-[#F8F5F0] font-serif-display italic text-[20rem] leading-none select-none pointer-events-none hidden lg:block">
        b
      </div>
    </section>
  );
}
